import React, { useCallback, createElement, useEffect, type SyntheticEvent, useRef } from 'react';
import { Journeys, useCrossFlow } from '@atlassiansox/cross-flow-support';

import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import type { ButtonProps } from '@atlaskit/button';
import { IconButton } from '@atlaskit/button/new';
import MoreIcon from '@atlaskit/icon/glyph/more';

import { LinkItem, Section } from '@atlaskit/menu';
import { Inline, xcss } from '@atlaskit/primitives';
import { AppRecommendationsAdControl } from '@atlassian/app-recommendations-kit';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import { useCustomAnalyticEvents } from '../../../common/analytics-hooks';
import {
	ENTITY_CONFIG_DEFINITION,
	PACKAGE_NAME,
	EVENT_SOURCE_RECOMMENDATION,
	TEAM_NAME,
	ENTITY_ID_TO_TARGET_TYPE,
	type CohortType,
	type EntityConfigType,
	type RecommendationEntityIdType,
	type iconType,
} from '../../../common/constants';

export interface RecommendationSectionProps {
	entityId: RecommendationEntityIdType | undefined;
	sessionId: string;
	isRecommendationLoaded: boolean;
	cohort: CohortType | string;
}

// Replace with lodash/noop
// eslint-disable-next-line @typescript-eslint/no-empty-function
const suspensePromise = new Promise(() => {});

export const RecommendationSection = ({
	entityId,
	sessionId,
	isRecommendationLoaded,
	cohort,
}: RecommendationSectionProps) => {
	const linkItemRef = useRef<HTMLElement | null>(null);
	const { fireViewedEvent, fireClickEvent } = useCustomAnalyticEvents({ cohort, sessionId });
	const crossFlow = useCrossFlow();

	const onLinkClick = useCallback(
		async (event: SyntheticEvent) => {
			// We need isolate clicks on the link from the clicks to the ARC elements
			if (
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				(event.target as unknown as HTMLElement).closest('a') === linkItemRef.current &&
				entityId // just to explain to TS that entityId is not undefined in this case
			) {
				fireClickEvent({ eventName: EVENT_SOURCE_RECOMMENDATION, entityId });

				try {
					if (!crossFlow.isEnabled) {
						throw new Error('Cross Flow not enabled');
					}

					await crossFlow.api.open({
						sourceComponent: EVENT_SOURCE_RECOMMENDATION,
						sourceContext: EVENT_SOURCE_RECOMMENDATION,
						targetProduct: ENTITY_ID_TO_TARGET_TYPE[entityId],
						journey: Journeys.DECIDE,
						extraAnalyticsAttributes: {
							recommendedEntityId: entityId,
							recommendationSessionId: sessionId,
						},
					});
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
				} catch (error: any) {
					fireErrorAnalytics({
						meta: {
							id: `openCrossFlow.${EVENT_SOURCE_RECOMMENDATION}`,
							packageName: PACKAGE_NAME,
							teamName: TEAM_NAME,
						},
						error,
					});
				}
			}
		},
		[sessionId, crossFlow, entityId, fireClickEvent],
	);

	let entityConfig: EntityConfigType | undefined;

	// If recommendation is received show the recommendation otherwise return null
	if (entityId) {
		entityConfig = ENTITY_CONFIG_DEFINITION[entityId];
		// Show a placeholder while waiting for the recommendation reponse
	} else if (!isRecommendationLoaded) {
		throw suspensePromise;
	}

	useEffect(() => {
		if (entityConfig && entityId) {
			fireViewedEvent({ entityId });
		} else if (entityId) {
			throw new Error(`Entity ID ${entityId} not found in entity config`);
		}
	}, [entityConfig, fireViewedEvent, entityId]);

	return entityConfig && entityId ? (
		<Section
			title="Recommended Atlassian Products"
			hasSeparator
			testId="apps-1p-recs.ui.recommendation.recommendation-section.recos-section"
		>
			<LinkItem
				iconBefore={<LogoWrapper icon={entityConfig.icon} />}
				description={entityConfig.description}
				onClick={onLinkClick}
				ref={(ref) => {
					linkItemRef.current = ref;
				}}
				iconAfter={<ArcWrapper entityId={entityId} name={entityConfig.title} />}
			>
				{entityConfig.title}
			</LinkItem>
		</Section>
	) : null;
};

const ArcWrapper = ({ entityId, name }: { entityId: string; name: string }) => {
	// We need to stop click event propagation to the LinkItem so we can tell them apart
	const onMoreClick = useCallback(
		(onClick: ButtonProps['onClick']) =>
			(event: React.MouseEvent<HTMLElement, MouseEvent>, analyticsEvent: UIAnalyticsEvent) => {
				event.stopPropagation();

				onClick?.(event, analyticsEvent);
			},
		[],
	);

	return (
		<AppRecommendationsAdControl
			productId={entityId}
			trigger={({ triggerRef, onClick, ...triggerProps }) => (
				<IconButton
					icon={MoreIcon}
					{...triggerProps}
					onClick={onMoreClick(onClick)}
					spacing="compact"
					appearance="subtle"
					label="more"
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					ref={triggerRef as React.Ref<HTMLButtonElement>}
				/>
			)}
			name={name}
			reasons={['the size of your team', 'your existing Atlassian products']}
		/>
	);
};

const LogoWrapper = ({ icon }: { icon: iconType }) => (
	<Inline xcss={logoWrapperStyles}>
		{createElement(icon, { appearance: 'brand', size: 'small' })}
	</Inline>
);

const logoWrapperStyles = xcss({
	borderColor: 'color.border',
	borderStyle: 'solid',
	borderRadius: 'border.radius.100',
	borderWidth: 'border.width',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	height: 'size.300',
	width: 'size.300',
});
